// Period close & locking (PRD 08 §9). All business-scoped — the X-Business-Id
// header rides along through businessApi*.
//
// Close and reopen carry an Idempotency-Key: a retried close must not produce
// a second PeriodClose row, and the server replays the original when the same
// key arrives twice within 24 hours.

import {
  businessApiGet,
  businessApiPost,
} from '../utils/apiUtils';
import { buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import {
  ClosePeriodParams,
  ClosePeriodResult,
  CursorPage,
  DriftReport,
  ISODate,
  PeriodCloseRow,
  PeriodCloseStatus,
  PreCloseCheck,
  ReopenPeriodParams,
  ReopenPeriodResult,
} from '../types/businessApi';

const BASE = '/api/v1/business/period-close';

/**
 * One key per user intent, not per request: generated when the modal opens
 * so a double-submit or a retry after a dropped response collapses into the
 * same close.
 */
export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `close-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const idempotent = (key?: string) =>
  key ? { headers: { 'Idempotency-Key': key } } : undefined;

class BusinessPeriodCloseService {
  /** GET /period-close — current lock status. Readable by every member. */
  async getStatus(): Promise<PeriodCloseStatus> {
    const response = await businessApiGet(BASE);
    return parseData<PeriodCloseStatus>(response);
  }

  /** GET /period-close/pre-close-check?as_of= — blockers and warnings. */
  async preCloseCheck(asOf: ISODate): Promise<PreCloseCheck> {
    const response = await businessApiGet(
      `${BASE}/pre-close-check${buildQuery({ as_of: asOf })}`
    );
    return parseData<PreCloseCheck>(response);
  }

  /**
   * POST /period-close — close the books. Throws BusinessApiError with code
   * `pre_close_blocked` (details: PreCloseCheck) when blockers stand and the
   * caller did not force.
   */
  async close(params: ClosePeriodParams, idempotencyKey?: string): Promise<ClosePeriodResult> {
    const response = await businessApiPost(BASE, params, idempotent(idempotencyKey));
    return parseData<ClosePeriodResult>(response);
  }

  /** POST /period-close/reopen — requires a reason; password when one is set. */
  async reopen(params: ReopenPeriodParams, idempotencyKey?: string): Promise<ReopenPeriodResult> {
    const response = await businessApiPost(
      `${BASE}/reopen`,
      params,
      idempotent(idempotencyKey)
    );
    return parseData<ReopenPeriodResult>(response);
  }

  /** GET /period-close/history — newest first, cursor paginated. */
  async history(cursor?: string, limit?: number): Promise<CursorPage<PeriodCloseRow>> {
    const response = await businessApiGet(
      `${BASE}/history${buildQuery({ cursor, limit })}`
    );
    const { data, meta } = await parseEnvelope<PeriodCloseRow[]>(response);
    return { rows: data ?? [], nextCursor: (meta.cursor as string | null) ?? null };
  }

  /**
   * GET /period-close/drift-report — how far the books have moved since the
   * last close. 404s when nothing has been closed yet.
   */
  async driftReport(): Promise<DriftReport> {
    const response = await businessApiGet(`${BASE}/drift-report`);
    return parseData<DriftReport>(response);
  }
}

export const businessPeriodCloseService = new BusinessPeriodCloseService();
